import {
    createContext, Dispatch,
    FC,
    ReactNode, SetStateAction,
    useContext, useRef,
    useState,
} from "react";
import {Window, WindowActionsEvent} from "@progress/kendo-react-dialogs";
import {useTranslation} from "react-i18next";
import StartJobConfirmationPanel, {
    StartJobConfirmationPanelProps
} from "@pages/orders/preview/popups/StartJobConfirmationPanel";
import DelegateOrderPanel, {DelegateOrderPanelProps} from "@pages/orders/preview/popups/DelegateOrderPanel";
import UpdateProjectLeaderPanel from "@pages/orders/preview/popups/UpdateProjectLeaderPanel";
import UpdateConfirmedDateComponent from "@pages/orders/preview/popups/UpdateConfirmedDateComponent";
import AssignCoordinatorComponent from "@pages/orders/preview/popups/AssignCoordinatorComponent";
import AddDeliveryDetailsComponent from "@pages/orders/preview/popups/AddDeliveryDetailsComponent";
import MissingAttachmentsComponent from "@pages/orders/preview/popups/MissingAttachmentsComponent";
import {
    OrderAttachmentSummaryValidationModel, OrderEditSaveSummaryModel,
    OrderPreviewModelStateType
} from "@pages/orders/order.models";
import ResultsDeliveredUpdateComponent from "@pages/orders/preview/popups/ResultsDeliveredUpdateComponent";
import {GUID, VivoPortal} from "../AppConstants";
import InkComponentEditComponent
    from "@components/page_components/administration/inkseries/InkComponentEditComponent";
import CompanyListForUserComponent from "@components/page_components/users/edit/CompanyListForUserComponent";
import PrepareOrderForCompanyPopup from "@pages/orders/edit/popups/PrepareOrderForCompanyPopup";
import {NameWithId} from "../Models";
import TextAreaInputDialog from "./common/TextAreaInputDialog";
import OrderEditSaveConfirmationPanel from "@pages/orders/edit/popups/OrderEditSaveConfirmationPanel";
import EnumRedirectAfterSave = VivoPortal.EnumRedirectAfterSave;
import CalendarSelectionDialog from "@utils/popups/common/CalendarSelectionDialog";
import SendEmailPopup from "@pages/administration/mailings/SendEmailPopup";
import ImportComponentsPanel from "@pages/administration/components/popups/ImportComponentsPanel";
import {ImportComponentsResponse} from "@pages/administration/components/component.models";
import EditorInputDialog from "@utils/popups/common/EditorInputDialog";


// Create Context
export const VivoPopupContext = createContext<VivoPopupContextProps | null>(null);

// Custom Hook
export function useVivoPopupContext() {
    const context = useContext(VivoPopupContext);

    if (!context) {
        throw new Error(
            "useVivoPopupContext must be used within an VivoPopupContextProvider"
        );
    }

    return context;
}

// Provider Component
export const VivoPopupContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const {t} = useTranslation();
    const resultsDeliveredPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType,
        warnIfNotFilled: boolean
    }>({warnIfNotFilled: false, orderPreviewModel: null!});
    const missingAttachmentsPropsRef = useRef<{
        isLoading: boolean,
        model: OrderAttachmentSummaryValidationModel
    }>({isLoading: false, model: null!});
    const inkComponentEditComponentPropsRef = useRef<{
        componentGuid?: GUID
    }>({});
    const addDeliveryDetailsComponentPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const assignCoordinatorPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const startJobConfirmationPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const delegateOrderPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const updateProjectLeaderPanelPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const updateConfirmedDateComponentPropsRef = useRef<{
        orderPreviewModel: OrderPreviewModelStateType
    }>({orderPreviewModel: null!});
    const companyListForUserComponentRef = useRef<{
        labId: number,
        divisionId: number
    }>({labId: null!, divisionId: null!});
    const textAreaInputDialogRef = useRef<{
        title: string;
        description?: string;
    }>({title: null!});
    const editorInputDialogRef = useRef<{
        title: string;
        description?: string;
        initialValue?: string;
        isRequired?: boolean;
    }>({title: null!});
    const calendarSelectionDialogRef = useRef<{
        description?: string;
    }>({});
    const orderEditSaveConfirmationPanelRef = useRef<{
        summaryModel: OrderEditSaveSummaryModel;
    }>({summaryModel: null!});

    // Manage visibility and callbacks for popups
    const [popupState, setPopupState] = useState<PopupState>({
        StartJob: null,
        DelegateOrder: null,
        UpdateProjectLeader: null,
        UpdateConfirmedDate: null,
        AssignCoordinator: null,
        AddDeliveryDetails: null,
        MissingAttachments: null,
        ResultsDeliveredUpdate: null,
        InkComponentEditComponent: null,
        CompanyListForUserComponent: null,
        PrepareOrderForCompanyPopup: null,
        TextAreaInputDialog: null,
        EditorInputDialog: null,
        OrderEditSaveConfirmationPanel: null,
        CalendarSelectionDialog: null,
        SendEmailPopup: null,
        ImportComponentsPanel: null,
    });

    function setV<T extends keyof PopupState>(key: T, value: PopupState[T]) {
        setPopupState((prev) => ({
            ...prev,
            [key]: value,
        }));
    }

    // == UNIVERSAL HANDLERS
    function triggerStart<T extends keyof PopupState, T1>(key: T): Promise<T1> {
        return new Promise<T1>((resolve, reject) => {
            setV(key, {resolve, reject})
        });
    }

    function handleCompleted<T extends keyof PopupState, T1>(key: T, args?: T1) {
        if (popupState[key]) {
            popupState[key]?.resolve(args);
        }
        setV(key, null);
    }

    function handleCancelled<T extends keyof PopupState>(key: T) {
        if (popupState[key]) {
            popupState[key]?.reject(new Error("Cancelled"));
        }
        setV(key, null);
    }

    return (
        <VivoPopupContext.Provider
            value={{
                popups: {
                    startWork: {
                        start: (model: OrderPreviewModelStateType) => {
                            startJobConfirmationPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("StartJob");
                        }
                    },
                    delegateOrder: {
                        start: (model: OrderPreviewModelStateType) => {
                            delegateOrderPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("DelegateOrder");
                        }
                    },
                    updateProjectLeader: {
                        start: (model: OrderPreviewModelStateType) => {
                            updateProjectLeaderPanelPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("UpdateProjectLeader");
                        }
                    },
                    updateConfirmedDate: {
                        start: (model: OrderPreviewModelStateType) => {
                            updateConfirmedDateComponentPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("UpdateConfirmedDate");
                        }
                    },
                    assignCoordinator: {
                        start: (model: OrderPreviewModelStateType) => {
                            assignCoordinatorPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("AssignCoordinator");
                        }
                    },
                    addDeliveryDetails: {
                        start: (model: OrderPreviewModelStateType) => {
                            addDeliveryDetailsComponentPropsRef.current = {orderPreviewModel: model};
                            return triggerStart("AddDeliveryDetails");
                        }
                    },
                    missingAttachments: {
                        start: (isLoading: boolean, model: OrderAttachmentSummaryValidationModel) => {
                            missingAttachmentsPropsRef.current = {isLoading, model};
                            return triggerStart("MissingAttachments")
                        }
                    },
                    resultsDeliveredUpdate: {
                        start: (orderPreviewModel: OrderPreviewModelStateType, warnIfNotFilled: boolean) => {
                            resultsDeliveredPropsRef.current = {orderPreviewModel, warnIfNotFilled}
                            return triggerStart("ResultsDeliveredUpdate")
                        }
                    },
                    inkComponentEditComponent: {
                        start: (componentGuid?: GUID) => {
                            inkComponentEditComponentPropsRef.current = {
                                componentGuid: componentGuid,
                            }
                            return triggerStart("InkComponentEditComponent")
                        }
                    },
                    companyListForUserComponent: {
                        start: (labId: number, divisionId: number) => {
                            companyListForUserComponentRef.current = {
                                labId: labId,
                                divisionId: divisionId
                            }
                            return triggerStart("CompanyListForUserComponent")
                        }
                    },
                    prepareOrderForCompanyPopup: {
                        start: () => {
                            return triggerStart("PrepareOrderForCompanyPopup")
                        }
                    },
                    textAreaInputDialog: {
                        start: (title: string, description?: string) => {
                            textAreaInputDialogRef.current = {title: title, description: description}
                            return triggerStart("TextAreaInputDialog")
                        }
                    },
                    editorInputDialog: {
                        start: (title: string, description?: string, initialValue?: string, isRequired?: boolean) => {
                            editorInputDialogRef.current = {
                                title: title,
                                description: description,
                                initialValue: initialValue,
                                isRequired: isRequired
                            }
                            return triggerStart("EditorInputDialog")
                        }
                    },
                    orderEditSaveConfirmationPanel: {
                        start: (summaryModel: OrderEditSaveSummaryModel) => {
                            orderEditSaveConfirmationPanelRef.current = {summaryModel: summaryModel}
                            return triggerStart("OrderEditSaveConfirmationPanel")
                        }
                    },
                    calendarSelectionDialog: {
                        start: (description?: string) => {
                            calendarSelectionDialogRef.current = {description: description}
                            return triggerStart("CalendarSelectionDialog")
                        }
                    },
                    sendEmailPopup: {
                        start: () => {
                            return triggerStart("SendEmailPopup")
                        }
                    },
                    importComponentsPanel: {
                        start: () => {
                            return triggerStart("ImportComponentsPanel")
                        }
                    },

                },
            }}>
            {children}
            {popupState.StartJob && (
                <Window
                    width={450}
                    height={550}
                    title={t("ORDER_START_PROCESSING")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("StartJob")}
                >
                    <StartJobConfirmationPanel
                        orderPreviewModelState={startJobConfirmationPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("StartJob")}
                        OnConfirmedAndSaved={() => handleCompleted("StartJob")}
                    />
                </Window>
            )}
            {popupState.DelegateOrder && (
                <Window
                    width={450}
                    height={450}
                    title={t("ORDER_DELEGATE_ORDER")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("DelegateOrder")}
                >
                    <DelegateOrderPanel
                        orderPreviewModelState={delegateOrderPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("DelegateOrder")}
                        OnConfirmedAndSaved={() => handleCompleted("DelegateOrder")}
                    />
                </Window>
            )}
            {popupState.UpdateProjectLeader && (
                <Window
                    width={450}
                    height={250}
                    title={t("ORDER_PREVIEW_UpdateProjectLeaderTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("UpdateProjectLeader")}
                >
                    <UpdateProjectLeaderPanel
                        orderPreviewModelState={updateProjectLeaderPanelPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("UpdateProjectLeader")}
                        OnConfirmedAndSaved={() => handleCompleted("UpdateProjectLeader")}
                    />
                </Window>
            )}
            {popupState.UpdateConfirmedDate && (
                <Window
                    width={450}
                    height={450}
                    title={t("ORDER_PREVIEW_ConfirmedDateUpdatePopupTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("UpdateConfirmedDate")}
                >
                    <UpdateConfirmedDateComponent
                        orderPreviewModelState={updateConfirmedDateComponentPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("UpdateConfirmedDate")}
                        OnConfirmedAndSaved={() => handleCompleted("UpdateConfirmedDate")}
                    />
                </Window>
            )}
            {popupState.AssignCoordinator && (
                <Window
                    width={450}
                    height={250}
                    title={t("ORDER_PREVIEW_PROJECT_SelectCoordinator")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("AssignCoordinator")}
                >
                    <AssignCoordinatorComponent
                        orderPreviewModelState={assignCoordinatorPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("AssignCoordinator")}
                        OnConfirmedAndSaved={() => handleCompleted("AssignCoordinator")}
                    />
                </Window>
            )}
            {popupState.AddDeliveryDetails && (
                <Window
                    width={600}
                    height={450}
                    title={t("ORDER_PREVIEW_TRACKINGNUMBER_PopupTitle")}
                    modal={true}
                    //closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("AddDeliveryDetails")}
                >
                    <AddDeliveryDetailsComponent
                        orderPreviewModelState={addDeliveryDetailsComponentPropsRef.current.orderPreviewModel}
                        OnCancelled={() => handleCancelled("AddDeliveryDetails")}
                        OnConfirmedAndSaved={() => handleCompleted("AddDeliveryDetails")}
                    />
                </Window>
            )}
            {popupState.MissingAttachments && (
                <Window
                    width={550}
                    height={450}
                    title={t("ORDER_PREVIEW_MissingAttachmentsTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("MissingAttachments")}
                >
                    <MissingAttachmentsComponent
                        isLoading={missingAttachmentsPropsRef.current.isLoading}
                        model={missingAttachmentsPropsRef.current.model!}
                        OnCancelled={() => handleCancelled("MissingAttachments")}
                        OnConfirmedAndSaved={() => handleCompleted("MissingAttachments")}
                    />
                </Window>
            )}
            {popupState.ResultsDeliveredUpdate && (
                <Window
                    width={800}
                    height={550}
                    title={t("ORDER_ORDER_SUMMARY_DATA_INFO")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("ResultsDeliveredUpdate")}
                >
                    <ResultsDeliveredUpdateComponent
                        orderPreviewModelState={resultsDeliveredPropsRef.current.orderPreviewModel}
                        WarnIfNotFilled={resultsDeliveredPropsRef.current.warnIfNotFilled}
                        OnCancelled={() => handleCancelled("ResultsDeliveredUpdate")}
                        OnConfirmedAndSaved={() => handleCompleted("ResultsDeliveredUpdate")}
                    />
                </Window>
            )}
            {popupState.InkComponentEditComponent && (
                <Window
                    width={550}
                    height={550}
                    title={inkComponentEditComponentPropsRef.current.componentGuid ? t("COMPONENTS_Edit") : t("COMPONENTS_AddNew")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("InkComponentEditComponent")}
                >
                    <InkComponentEditComponent
                        componentGuid={inkComponentEditComponentPropsRef.current.componentGuid}
                        OnCancelled={() => handleCancelled("InkComponentEditComponent")}
                        OnConfirmedAndSaved={() => handleCompleted("InkComponentEditComponent")}
                    />
                </Window>
            )}
            {popupState.CompanyListForUserComponent && (
                <Window
                    width={900}
                    height={650}
                    title={t("USER_FILTER_Customers")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("CompanyListForUserComponent")}
                >
                    <CompanyListForUserComponent
                        LabId={companyListForUserComponentRef.current.labId}
                        DivisionId={companyListForUserComponentRef.current.divisionId}
                        OnCancelled={() => handleCancelled("CompanyListForUserComponent")}
                        OnConfirmedAndSaved={(company) => handleCompleted("CompanyListForUserComponent",
                            company)}
                    />
                </Window>
            )}
            {popupState.PrepareOrderForCompanyPopup && (
                <Window
                    width={600}
                    height={350}
                    title={t("ORDER_PREPARE_FOR_COMPANY")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("PrepareOrderForCompanyPopup")}
                >
                    <PrepareOrderForCompanyPopup
                        OnCancelled={() => handleCancelled("PrepareOrderForCompanyPopup")}
                        OnConfirmedAndSaved={(company: NameWithId | void) =>
                            handleCompleted<"PrepareOrderForCompanyPopup", NameWithId | void>("PrepareOrderForCompanyPopup", company)}
                    />
                </Window>
            )}
            {popupState.TextAreaInputDialog && (
                <Window
                    width={600}
                    height={350}
                    title={textAreaInputDialogRef.current.title}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("TextAreaInputDialog")}
                >
                    <TextAreaInputDialog description={textAreaInputDialogRef.current.description}
                                         OnCancelled={() => handleCancelled("TextAreaInputDialog")}
                                         OnConfirmedAndSaved={(message: string | void) =>
                                             handleCompleted<"TextAreaInputDialog", string | void>("TextAreaInputDialog", message)}
                    />
                </Window>
            )}
            {popupState.EditorInputDialog && (
                <Window
                    width={800}
                    height={550}
                    title={editorInputDialogRef.current.title}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("EditorInputDialog")}
                >
                    <EditorInputDialog description={editorInputDialogRef.current.description}
                                       Required={editorInputDialogRef.current.isRequired}
                                       InitialValue={editorInputDialogRef.current.initialValue}
                                       OnCancelled={() => handleCancelled("EditorInputDialog")}
                                       OnConfirmedAndSaved={(message: string | void) =>
                                           handleCompleted<"EditorInputDialog", string | void>("EditorInputDialog", message)}
                    />
                </Window>
            )}

            {popupState.OrderEditSaveConfirmationPanel && (
                <Window
                    width={800}
                    height={670}
                    title={t("ORDER_EDIT_SaveConfirmationTitle")}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("OrderEditSaveConfirmationPanel")}
                >
                    <OrderEditSaveConfirmationPanel
                        saveOrderSummary={orderEditSaveConfirmationPanelRef.current.summaryModel}
                        OnCancelled={() => handleCancelled("OrderEditSaveConfirmationPanel")}
                        OnConfirmedAndSaved={(redirectType: EnumRedirectAfterSave | void) =>
                            handleCompleted<"OrderEditSaveConfirmationPanel", EnumRedirectAfterSave | void>("OrderEditSaveConfirmationPanel", redirectType)}
                    />
                </Window>
            )}
            {popupState.CalendarSelectionDialog && (
                <Window
                    width={500}
                    height={600}
                    title={"Select a date"}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("CalendarSelectionDialog")}
                >
                    <CalendarSelectionDialog description={calendarSelectionDialogRef.current.description}
                                             OnCancelled={() => handleCancelled("CalendarSelectionDialog")}
                                             OnConfirmedAndSaved={(selectedDate: Date | void) =>
                                                 handleCompleted<"CalendarSelectionDialog", Date | void>("CalendarSelectionDialog", selectedDate)}
                    />
                </Window>
            )}
            {popupState.SendEmailPopup && (
                <Window
                    width={800}
                    height={650}
                    title={"Quick component import"}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("SendEmailPopup")}
                >
                    <SendEmailPopup OnCancelled={() => handleCancelled("SendEmailPopup")}
                                    OnConfirmedAndSaved={(mailing: NameWithId | void) =>
                                        handleCompleted<"SendEmailPopup", NameWithId | void>("SendEmailPopup", mailing)}/>
                </Window>
            )}
            {popupState.ImportComponentsPanel && (
                <Window
                    width={700}
                    height={650}
                    title={"Select a date"}
                    modal={true}
                    // closeButton={() => <></>}
                    onClose={(args: WindowActionsEvent) => handleCancelled("ImportComponentsPanel")}
                >
                    <ImportComponentsPanel OnCancelled={() => handleCancelled("ImportComponentsPanel")}
                                           OnConfirmedAndSaved={(response: ImportComponentsResponse | void) =>
                                               handleCompleted<"ImportComponentsPanel", ImportComponentsResponse | void>("ImportComponentsPanel", response)}/>
                </Window>
            )}
        </VivoPopupContext.Provider>
    );
};

// Context Props
interface VivoPopupContextProps {
    popups: {
        startWork: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        delegateOrder: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        updateProjectLeader: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        updateConfirmedDate: {
            start: (model: OrderPreviewModelStateType) => Promise<void>;
        };
        assignCoordinator: {
            start: (orderPreviewModelState: OrderPreviewModelStateType) => Promise<void>;
        };
        addDeliveryDetails: {
            start: (orderPreviewModelState: OrderPreviewModelStateType) => Promise<void>;
        };
        missingAttachments: {
            start: (isLoading: boolean, model: OrderAttachmentSummaryValidationModel) => Promise<void>;
        };
        resultsDeliveredUpdate: {
            start: (orderPreviewModel: OrderPreviewModelStateType, warnIfNotFilled: boolean) => Promise<void>;
        };
        inkComponentEditComponent: {
            start: (componentGuid?: GUID) => Promise<void>;
        };
        companyListForUserComponent: {
            start: (labId: number, divisionId: number) => Promise<void>;
        };
        prepareOrderForCompanyPopup: {
            start: () => Promise<NameWithId>;
        };
        textAreaInputDialog: {
            start: (title: string, description?: string) => Promise<string>;
        };
        editorInputDialog: {
            start: (title: string, description?: string, initialValue?: string, isRequired?: boolean) => Promise<string>;            
        };
        orderEditSaveConfirmationPanel: {
            start: (summaryModel: OrderEditSaveSummaryModel) => Promise<EnumRedirectAfterSave>;
        };
        calendarSelectionDialog: {
            start: (description?: string) => Promise<Date>;
        };
        sendEmailPopup: {
            start: () => Promise<NameWithId>;
        };
        importComponentsPanel: {
            start: () => Promise<ImportComponentsResponse>;
        };

    };
}

interface PopupState {
    StartJob: PopupCallbacks | null;
    DelegateOrder: PopupCallbacks | null;
    UpdateProjectLeader: PopupCallbacks | null;
    UpdateConfirmedDate: PopupCallbacks | null;
    AssignCoordinator: PopupCallbacks | null;
    AddDeliveryDetails: PopupCallbacks | null;
    MissingAttachments: PopupCallbacks | null;
    ResultsDeliveredUpdate: PopupCallbacks | null;
    InkComponentEditComponent: PopupCallbacks | null;
    CompanyListForUserComponent: PopupCallbacks | null;
    PrepareOrderForCompanyPopup: PopupCallbacks | null;
    TextAreaInputDialog: PopupCallbacks | null;
    EditorInputDialog: PopupCallbacks | null;
    OrderEditSaveConfirmationPanel: PopupCallbacks | null;
    CalendarSelectionDialog: PopupCallbacks | null;
    SendEmailPopup: PopupCallbacks | null;
    ImportComponentsPanel: PopupCallbacks | null;
}

interface PopupCallbacks<T = null> {
    resolve: (args: any) => void;
    reject: (reason?: any) => void;
}
